import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Skeleton,
} from '@forgenetwork/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Users,
  Activity,
  RefreshCw,
  Download,
  ExternalLink,
} from 'lucide-react';

export const Route = createFileRoute('/admin/governance')({
  component: AdminGovernancePage,
});

interface AuditLog {
  id: string;
  action: string;
  targetType: string;
  targetId: string | null;
  timestamp: string;
  admin: {
    id: string;
    name: string;
    email: string;
  };
  metadata: Record<string, unknown>;
}

interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
  page: number;
  pageSize: number;
}

// Compliance framework types
type ComplianceFramework = 'SOC2' | 'HIPAA' | 'GDPR' | 'ISO27001' | 'PCI_DSS';
type ComplianceStatus = 'compliant' | 'non_compliant' | 'partial' | 'in_progress';

interface FrameworkCompliance {
  framework: ComplianceFramework;
  status: ComplianceStatus;
  score: number;
  controlsPassing: number;
  controlsTotal: number;
  lastAudit: string;
  nextAudit: string | null;
}

interface PolicyViolation {
  id: string;
  policy: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'in_progress' | 'resolved';
  detectedAt: string;
  affectedResource: string;
}

function AdminGovernancePage(): React.JSX.Element {
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Fetch audit logs
  const { data: auditData, isLoading: auditLoading, refetch: refetchAudit } = useQuery({
    queryKey: ['admin', 'audit-logs', 'governance'],
    queryFn: async () => {
      return api.get<AuditLogsResponse>('/admin/audit-logs?pageSize=10');
    },
  });

  // Generate mock governance data (in production, this would come from API)
  const governanceData = React.useMemo(() => {
    const frameworks: FrameworkCompliance[] = [
      {
        framework: 'SOC2',
        status: 'compliant',
        score: 94,
        controlsPassing: 58,
        controlsTotal: 64,
        lastAudit: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        nextAudit: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        framework: 'GDPR',
        status: 'compliant',
        score: 97,
        controlsPassing: 30,
        controlsTotal: 32,
        lastAudit: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        nextAudit: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        framework: 'ISO27001',
        status: 'partial',
        score: 89,
        controlsPassing: 98,
        controlsTotal: 114,
        lastAudit: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
        nextAudit: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        framework: 'HIPAA',
        status: 'in_progress',
        score: 85,
        controlsPassing: 40,
        controlsTotal: 45,
        lastAudit: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        nextAudit: null,
      },
    ];

    const violations: PolicyViolation[] = [
      {
        id: 'v1',
        policy: 'Data Retention Policy',
        description: 'User data older than retention period found in backup storage',
        severity: 'medium',
        status: 'in_progress',
        detectedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        affectedResource: 'backup-storage-east',
      },
      {
        id: 'v2',
        policy: 'Access Control Policy',
        description: 'Service account with excessive permissions detected',
        severity: 'high',
        status: 'open',
        detectedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        affectedResource: 'service-account-analytics',
      },
    ];

    const overallScore = Math.round(
      frameworks.reduce((sum, f) => sum + f.score, 0) / frameworks.length
    );

    return {
      overview: {
        complianceScore: overallScore,
        activeViolations: violations.filter(v => v.status !== 'resolved').length,
        criticalViolations: violations.filter(v => v.severity === 'critical' && v.status !== 'resolved').length,
        policiesEnforced: 24,
        lastAuditDate: frameworks.length > 0
          ? frameworks.reduce((latest, f) =>
              new Date(f.lastAudit) > new Date(latest) ? f.lastAudit : latest,
              frameworks[0]!.lastAudit
            )
          : new Date().toISOString(),
      },
      frameworks,
      violations,
    };
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetchAudit();
    setIsRefreshing(false);
  };

  const getStatusBadge = (status: ComplianceStatus) => {
    switch (status) {
      case 'compliant':
        return <Badge className="bg-green-500">Compliant</Badge>;
      case 'non_compliant':
        return <Badge variant="destructive">Non-Compliant</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-500">Partial</Badge>;
      case 'in_progress':
        return <Badge variant="secondary">In Progress</Badge>;
    }
  };

  const getSeverityBadge = (severity: PolicyViolation['severity']) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="destructive">Critical</Badge>;
      case 'high':
        return <Badge className="bg-orange-500">High</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-500">Medium</Badge>;
      case 'low':
        return <Badge variant="secondary">Low</Badge>;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Governance Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              Monitor compliance, policy enforcement, and audit trails
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Compliance Score</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{governanceData.overview.complianceScore}%</div>
              <p className="text-xs text-muted-foreground">Across all frameworks</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Violations</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{governanceData.overview.activeViolations}</div>
              <p className="text-xs text-muted-foreground">
                {governanceData.overview.criticalViolations} critical
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Policies Enforced</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{governanceData.overview.policiesEnforced}</div>
              <p className="text-xs text-muted-foreground">Active policies</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last Audit</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatTimeAgo(governanceData.overview.lastAuditDate)}
              </div>
              <p className="text-xs text-muted-foreground">Most recent assessment</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Audit Events</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{auditData?.total ?? 0}</div>
              <p className="text-xs text-muted-foreground">Total logged events</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Compliance Frameworks */}
          <Card>
            <CardHeader>
              <CardTitle>Compliance Frameworks</CardTitle>
              <CardDescription>Status across regulatory frameworks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {governanceData.frameworks.map((framework) => (
                  <div
                    key={framework.framework}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="font-medium">{framework.framework}</div>
                      {getStatusBadge(framework.status)}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm font-medium">{framework.score}%</div>
                        <div className="text-xs text-muted-foreground">
                          {framework.controlsPassing}/{framework.controlsTotal} controls
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Policy Violations */}
          <Card>
            <CardHeader>
              <CardTitle>Policy Violations</CardTitle>
              <CardDescription>Active compliance issues requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {governanceData.violations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mb-2 text-green-500" />
                  <p>No active violations</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {governanceData.violations.map((violation) => (
                    <div
                      key={violation.id}
                      className="p-3 rounded-lg border space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{violation.policy}</div>
                        {getSeverityBadge(violation.severity)}
                      </div>
                      <p className="text-sm text-muted-foreground">{violation.description}</p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Resource: {violation.affectedResource}</span>
                        <span>{formatTimeAgo(violation.detectedAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Audit Trail */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Audit Trail</CardTitle>
            <CardDescription>Latest administrative actions and system events</CardDescription>
          </CardHeader>
          <CardContent>
            {auditLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 border rounded-lg">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : auditData?.logs && auditData.logs.length > 0 ? (
              <div className="space-y-2">
                {auditData.logs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="bg-primary/10 rounded-full p-2">
                      <Users className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">
                        {log.action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        by {log.admin?.email ?? 'System'} on {log.targetType}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatTimeAgo(log.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No audit logs available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
